import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { SafetyTestService } from '../../core/services/safety-test.service';
import { SafetyTestCategoryService } from '../../core/services/safety-test-category.service';
import { SafetyTest, SafetyTestCategory, AdminTestStatus } from '../../core/models';

@Component({
  selector: 'app-safety-tests',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './safety-tests.component.html',
  styleUrl: './safety-tests.component.scss'
})
export class SafetyTestsComponent implements OnInit {
  tests: SafetyTest[] = [];
  allTests: SafetyTest[] = [];
  categories: SafetyTestCategory[] = [];
  loading = true;
  errorMessage = '';

  // Pagination
  currentPage = 1;
  pageSize = 20;
  totalTests = 0;
  totalPages = 0;

  // Filters
  searchQuery = '';
  filterStatus: 'all' | AdminTestStatus = 'all';
  filterCategory: string = 'all';
  sortBy: 'createdAt' | 'title' | 'version' = 'createdAt';
  sortOrder: 'asc' | 'desc' = 'desc';

  constructor(
    private router: Router,
    private safetyTestService: SafetyTestService,
    private categoryService: SafetyTestCategoryService
  ) {}

  ngOnInit(): void {
    this.loadCategories();
    this.loadTests();
  }

  async loadCategories(): Promise<void> {
    try {
      this.categories = await this.categoryService.listCategories({ systemOnly: true });
    } catch (error: any) {
      console.error('Failed to load categories:', error);
    }
  }

  async loadTests(): Promise<void> {
    this.loading = true;
    this.errorMessage = '';

    try {
      const filters: any = {};

      if (this.filterStatus !== 'all') {
        filters.status = this.filterStatus;
      }

      if (this.filterCategory !== 'all') {
        filters.categoryId = this.filterCategory;
      }

      this.allTests = await this.safetyTestService.listTests(filters);
      this.applyFiltersAndPagination();
    } catch (error: any) {
      console.error('Load tests error:', error);
      this.errorMessage = 'Failed to load tests';
    } finally {
      this.loading = false;
    }
  }

  private applyFiltersAndPagination(): void {
    let filteredTests = [...this.allTests];

    // Apply search filter (client-side)
    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      filteredTests = filteredTests.filter(test =>
        test.title.toLowerCase().includes(query) ||
        (test.shortName?.toLowerCase().includes(query)) ||
        (test.description?.toLowerCase().includes(query))
      );
    }

    // Apply sorting
    filteredTests.sort((a, b) => {
      let comparison = 0;
      switch (this.sortBy) {
        case 'title':
          comparison = a.title.localeCompare(b.title);
          break;
        case 'version':
          comparison = a.version - b.version;
          break;
        case 'createdAt':
        default:
          comparison = new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime();
          break;
      }
      return this.sortOrder === 'desc' ? -comparison : comparison;
    });

    this.totalTests = filteredTests.length;
    this.totalPages = Math.ceil(this.totalTests / this.pageSize);

    // Apply pagination
    const startIndex = (this.currentPage - 1) * this.pageSize;
    this.tests = filteredTests.slice(startIndex, startIndex + this.pageSize);
  }

  onSearch(): void {
    this.currentPage = 1;
    this.applyFiltersAndPagination();
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.loadTests();
  }

  showClearFilters(): boolean {
    return !!(this.searchQuery || this.filterStatus !== 'all' || this.filterCategory !== 'all' || this.sortBy !== 'createdAt' || this.sortOrder !== 'desc');
  }

  resetFilters(): void {
    this.searchQuery = '';
    this.filterStatus = 'all';
    this.filterCategory = 'all';
    this.sortBy = 'createdAt';
    this.sortOrder = 'desc';
    this.onFilterChange();
  }

  onSortChange(): void {
    this.applyFiltersAndPagination();
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.applyFiltersAndPagination();
    }
  }

  viewTest(testId: string): void {
    this.router.navigate(['/safety-tests', testId, 'details']);
  }

  editTest(testId: string): void {
    this.router.navigate(['/safety-tests', testId, 'edit']);
  }

  createTest(): void {
    this.router.navigate(['/safety-tests/create']);
  }

  // Category Modal
  private categoryModal: any;
  editingCategory: SafetyTestCategory | null = null;
  categoryForm = { name: '', shortName: '', description: '' };
  savingCategory = false;
  deletingCategory = false;

  openCategoryModal(): void {
    this.editingCategory = null;
    this.categoryForm = { name: '', shortName: '', description: '' };
    const modalElement = document.getElementById('categoryModal');
    if (modalElement) {
      this.categoryModal = new (window as any).bootstrap.Modal(modalElement);
      this.categoryModal.show();
    }
  }

  editCategory(category: SafetyTestCategory): void {
    this.editingCategory = category;
    this.categoryForm = {
      name: category.name,
      shortName: category.shortName || '',
      description: category.description || ''
    };
  }

  async saveCategory(): Promise<void> {
    if (!this.categoryForm.name.trim()) return;

    this.savingCategory = true;
    try {
      if (this.editingCategory) {
        await this.categoryService.updateCategory(this.editingCategory.id, {
          name: this.categoryForm.name,
          shortName: this.categoryForm.shortName || undefined,
          description: this.categoryForm.description || undefined
        });
      } else {
        await this.categoryService.createCategory({
          name: this.categoryForm.name,
          shortName: this.categoryForm.shortName || undefined,
          description: this.categoryForm.description || undefined,
          isSystem: true
        });
      }
      await this.loadCategories();
      this.editingCategory = null;
      this.categoryForm = { name: '', shortName: '', description: '' };
    } catch (error: any) {
      console.error('Failed to save category:', error);
      this.errorMessage = 'Failed to save category';
    } finally {
      this.savingCategory = false;
    }
  }

  async deleteCategory(category: SafetyTestCategory): Promise<void> {
    if (!confirm(`Are you sure you want to delete "${category.name}"?`)) return;

    this.deletingCategory = true;
    try {
      await this.categoryService.deleteCategory(category.id);
      await this.loadCategories();
    } catch (error: any) {
      console.error('Failed to delete category:', error);
      this.errorMessage = 'Failed to delete category';
    } finally {
      this.deletingCategory = false;
    }
  }

  cancelEditCategory(): void {
    this.editingCategory = null;
    this.categoryForm = { name: '', shortName: '', description: '' };
  }

  viewAnalytics(): void {
    this.router.navigate(['/safety-tests/analytics']);
  }

  formatDate(dateString: string | undefined): string {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  getStatusClass(status: AdminTestStatus): string {
    switch (status) {
      case 'active': return 'bg-success';
      case 'draft': return 'bg-secondary';
      case 'retired': return 'bg-warning';
      default: return 'bg-secondary';
    }
  }

  getStatusLabel(status: AdminTestStatus): string {
    switch (status) {
      case 'active': return 'Active';
      case 'draft': return 'Draft';
      case 'retired': return 'Retired';
      default: return status;
    }
  }

  getCategoryName(categoryId: string | undefined): string {
    if (!categoryId) return '—';
    const category = this.categories.find(c => c.id === categoryId);
    return category?.shortName || category?.name || '—';
  }

  get paginationRange(): number[] {
    const range: number[] = [];
    const maxButtons = 5;
    let start = Math.max(1, this.currentPage - Math.floor(maxButtons / 2));
    let end = Math.min(this.totalPages, start + maxButtons - 1);

    if (end - start < maxButtons - 1) {
      start = Math.max(1, end - maxButtons + 1);
    }

    for (let i = start; i <= end; i++) {
      range.push(i);
    }

    return range;
  }

  readonly Math = Math;
}
